import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Segment } from 'tea-component';
import classNames from 'classnames';
import { useSelector } from 'umi';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import { getName } from '@home/utils/common';
import dashboardRequest from '@/pages/dashboard/utils/dashboardRequest';
import KpiOverallPerformanceDimensionTrendService from '../KpiOverallPerformanceDimensionTrendService';
import {
  kpiDimensionMetricsTrendBeforeRequest,
} from '../KpiEventDimensionTrendService/_utils';
import { DIMENSION_DEFAULT_TRACK_DATA } from '../KpiEventDimensionTrendService/_type';
import {
  APPLOVIN_SKIP_REQUEST_KEY,
  createApplovinEmptyOverallPerformanceResponse,
  mergeApplovinRegionCodesConfigLayers,
  mergeApplovinRegionCodesForMode,
  mergeApplovinRegionIntoTrendPayload,
  normalizeCodesConfig,
  pickApplovinRegionCodesFlat,
  resolveApplovinCodesFromSelectorItems,
  resolveApplovinFilterList,
  resolveOsListForRequest,
  type IApplovinRegionCodesByMode,
  type IApplovinRegionCodesConfig,
  type TApplovinRegionMode,
  validateUsMarketSelection,
} from './applovinRegionTrendUtils';
import './index.scss';

/** Segment 每一项：value 可任意配置；regionMode 决定 Global/US 请求逻辑 */
export interface IApplovinRegionSegmentOption {
  value: string;
  textCn: string;
  textEn: string;
  /** 与 Tab 逻辑对应：global = 不分国家；us = 分国家+美国 */
  regionMode: TApplovinRegionMode;
}

export interface IApplovinRegionTabConfig {
  /** 是否展示 Segment，默认 true */
  segmentVisible?: boolean;
  /** 默认选中的 segment value，须与 segmentOptions 中某项一致；缺省取第一项 */
  defaultTabValue?: string;
  segmentOptions?: IApplovinRegionSegmentOption[];
  segmentClassName?: string;
}

const inferRegionMode = (
  opt: IApplovinRegionSegmentOption | undefined,
): TApplovinRegionMode => {
  if (opt?.regionMode) {
    return opt.regionMode;
  }
  const v = String(opt?.value ?? '').toLowerCase();
  return v === 'us' ? 'us' : 'global';
};

const resolveRegionModeByValue = (
  tabValue: string,
  options: IApplovinRegionSegmentOption[],
): TApplovinRegionMode => {
  const hit = options.find(o => o.value === tabValue);
  return inferRegionMode(hit);
};

/**
 * Applovin D7 / New Users：请求体 custom_filters 中通过 ua_network（或 applovinCustomFilterKey）传买量渠道。
 * Applovin 相关取值为后端枚举 code（如 applovin_adn、applovin_exchange），须在配置中通过
 * `allowedUaNetworkCodes`（或旧名 applovinChannelCodes）列出；未配置时从 selector 同 key 下按 code 子串兜底匹配。
 *
 * OS / 国家：`allowedOsCodes`、`allowedMarketsListCode`（与 allowedOsCodes 同为 code 列表，含 255 / us 等）；
 * `allowedUaNetworkCodes` 用法同 `allowedOsCodes`。
 *
 * 可选 `applovinCodesByRegionMode: { global?: {...}, us?: {...} }`：按 Tab 合并码表；Global 不做国家与 allowedMarketsListCode 交集校验。
 */

const DEFAULT_OVERALL_PERFORMANCE_API =
  '/api/v1/dashboard/pc/overview_daily/overall_performance';

/**
 * KpiApplovinRegionTrendService props：为外层薄适配层，
 * 接收大量配置字段，其余透传到底层图表 Service；此处仅用 Record 收敛，避免逐一罗列。
 */
interface IKpiApplovinRegionTrendServiceProps {
  config?: Record<string, unknown>;
  applovinRegionTabConfig?: unknown;
  applovinChannelCodes?: unknown;
  allowedUaNetworkCodes?: unknown;
  applovinCustomFilterKey?: unknown;
  applovinCodesConfig?: unknown;
  allowedOsCodes?: unknown;
  applovinCodesByRegionMode?: unknown;
  // 其余 props 会透传给下游组件（如 api/beforeRequest 等）
  [key: string]: unknown;
}

const KpiApplovinRegionTrendService: React.FC<IKpiApplovinRegionTrendServiceProps> = (
  props,
) => {
  const {
    config: userConfig,
    applovinRegionTabConfig: applovinRegionTabConfigProp,
    applovinChannelCodes: applovinChannelCodesProp,
    allowedUaNetworkCodes: allowedUaNetworkCodesProp,
    applovinCustomFilterKey: applovinCustomFilterKeyProp,
    applovinCodesConfig: applovinCodesConfigProp,
    allowedOsCodes: allowedOsCodesProp,
    allowedMarketsListCode: allowedMarketsListCodeProp,
    applovinCodeMatchSubstring: applovinCodeMatchSubstringProp,
    applovinCodesByRegionMode: applovinCodesByRegionModeProp,
    api: apiFromParent,
    className: classNameProp,
    ...rest
  } = props || {};
  const { isEN } = useLocal();

  const applovinRegionTabConfig: IApplovinRegionTabConfig | undefined =
    (userConfig?.applovinRegionTabConfig as IApplovinRegionTabConfig | undefined)
    ?? (applovinRegionTabConfigProp as IApplovinRegionTabConfig | undefined);
  /** 与 custom_filters.ua_network.filter_list 对应的 code 列表；兼容 applovinUaNetworkCodes、applovinChannelCodes */
  const configuredUaNetworkCodes: string[] | undefined =
    (userConfig?.allowedUaNetworkCodes as string[] | undefined) ??
    (userConfig?.applovinUaNetworkCodes as string[] | undefined) ??
    (userConfig?.applovinChannelCodes as string[] | undefined) ??
    (allowedUaNetworkCodesProp as string[] | undefined) ??
    (applovinChannelCodesProp as string[] | undefined);
  const applovinCustomFilterKey: string =
    (userConfig?.applovinCustomFilterKey as string | undefined) ??
    (applovinCustomFilterKeyProp as string | undefined) ??
    'ua_network';
  const mergedApplovinCodesConfigInput: IApplovinRegionCodesConfig | undefined =
    useMemo(
      () =>
        mergeApplovinRegionCodesConfigLayers(
          // 透传类配置：上游确定结构，此处仅做形状断言避免 unknown 泄漏到业务逻辑
          userConfig?.applovinCodesConfig as Partial<IApplovinRegionCodesConfig> | undefined,
          applovinCodesConfigProp as Partial<IApplovinRegionCodesConfig> | undefined,
          pickApplovinRegionCodesFlat(userConfig as Record<string, unknown>),
          pickApplovinRegionCodesFlat({
            allowedOsCodes: allowedOsCodesProp,
            allowedMarketsListCode: allowedMarketsListCodeProp,
            applovinCodeMatchSubstring: applovinCodeMatchSubstringProp,
          }),
        ),
      [
        userConfig,
        applovinCodesConfigProp,
        allowedOsCodesProp,
        allowedMarketsListCodeProp,
        applovinCodeMatchSubstringProp,
      ],
    );

  const applovinCodesByRegionMode: IApplovinRegionCodesByMode | undefined =
    (userConfig?.applovinCodesByRegionMode as IApplovinRegionCodesByMode | undefined)
    ?? (applovinCodesByRegionModeProp as IApplovinRegionCodesByMode | undefined);

  const resolveNormalizedCodesForMode = useCallback(
    (regionMode: TApplovinRegionMode) =>
      normalizeCodesConfig(
        mergeApplovinRegionCodesForMode(
          mergedApplovinCodesConfigInput,
          applovinCodesByRegionMode,
          regionMode,
        ),
      ),
    [mergedApplovinCodesConfigInput, applovinCodesByRegionMode],
  );

  const segmentOptionsFlat: IApplovinRegionSegmentOption[] = useMemo(() => {
    const raw = applovinRegionTabConfig?.segmentOptions;
    if (raw && raw.length > 0) {
      return raw.map(o => ({
        ...o,
        regionMode: o.regionMode ?? inferRegionMode(o),
      }));
    }
    return [
      {
        value: 'global',
        textCn: 'Global',
        textEn: 'Global',
        regionMode: 'global',
      },
      {
        value: 'us',
        textCn: 'US',
        textEn: 'US',
        regionMode: 'us',
      },
    ];
  }, [applovinRegionTabConfig?.segmentOptions]);

  const defaultSegmentValue = useMemo(() => {
    const configured = applovinRegionTabConfig?.defaultTabValue;
    if (
      configured &&
      segmentOptionsFlat.some(o => o.value === configured)
    ) {
      return configured;
    }
    return segmentOptionsFlat[0]?.value ?? 'global';
  }, [applovinRegionTabConfig?.defaultTabValue, segmentOptionsFlat]);

  const [segmentValue, setSegmentValue] = useState<string>(defaultSegmentValue);

  useEffect(() => {
    setSegmentValue(defaultSegmentValue);
  }, [defaultSegmentValue]);

  const regionModeForSegment = useMemo(
    () => resolveRegionModeByValue(segmentValue, segmentOptionsFlat),
    [segmentValue, segmentOptionsFlat],
  );

  const codesConfig = useMemo(
    () => resolveNormalizedCodesForMode(regionModeForSegment),
    [resolveNormalizedCodesForMode, regionModeForSegment],
  );

  const applovinCodesFingerprint = useMemo(
    () =>
      JSON.stringify({
        byMode: applovinCodesByRegionMode ?? null,
        allowedOsCodes: codesConfig.allowedOsCodes,
        allowedMarketsListCode: codesConfig.allowedMarketsListCode,
        applovinCodeMatchSubstring: codesConfig.applovinCodeMatchSubstring,
        extra: codesConfig.extraCustomFilterDefaults,
        allowedUaNetworkCodes: configuredUaNetworkCodes?.slice().sort() ?? [],
        regionMode: regionModeForSegment,
      }),
    [
      applovinCodesByRegionMode,
      codesConfig,
      configuredUaNetworkCodes,
      regionModeForSegment,
    ],
  );

  // useSelector 的 state 形状由 DVA 拼装，此处仅读 dashboardSetting.selector；使用局部结构类型
  const selector = useSelector(
    (s: { dashboardSetting?: { selector?: Record<string, unknown>; }; }) =>
      s?.dashboardSetting?.selector,
  ) as
    | Record<string, unknown>
    | undefined;

  const resolvedApplovinCodes = useMemo(() => {
    if (
      Array.isArray(configuredUaNetworkCodes) &&
      configuredUaNetworkCodes.length > 0
    ) {
      return configuredUaNetworkCodes.map(String);
    }
    const items = selector?.[applovinCustomFilterKey] as
      | Array<{ code?: string; }>
      | undefined;
    return resolveApplovinCodesFromSelectorItems(
      items,
      codesConfig.applovinCodeMatchSubstring,
    );
  }, [
    configuredUaNetworkCodes,
    applovinCustomFilterKey,
    selector,
    codesConfig.applovinCodeMatchSubstring,
  ]);

  const segmentOptions = useMemo(
    () =>
      segmentOptionsFlat.map(o => ({
        value: o.value,
        text: getName(isEN, o.textEn, o.textCn),
      })),
    [segmentOptionsFlat, isEN],
  );

  const segmentVisible = applovinRegionTabConfig?.segmentVisible !== false;

  const skipPayload = useMemo(
    () => ({ [APPLOVIN_SKIP_REQUEST_KEY]: true }),
    [],
  );

  const beforeRequest = useCallback(
    // beforeRequest 的入参由 Service 聚合 filterVal/trackData 后注入，形态由上游运行时决定
    (data: {
      filterVal?: Record<string, unknown>;
      applovinRegionTab?: string;
      [key: string]: unknown;
    }): Record<string, unknown> => {
      const filterVal = data?.filterVal as Record<string, unknown> | undefined;
      const tabVal = String(data?.applovinRegionTab ?? '');
      const regionMode = resolveRegionModeByValue(tabVal, segmentOptionsFlat);
      const codesForRequest = resolveNormalizedCodesForMode(regionMode);

      const osList = resolveOsListForRequest(filterVal, codesForRequest);
      if (osList === null) {
        return skipPayload;
      }

      const applovinFilterList = resolveApplovinFilterList(
        filterVal,
        applovinCustomFilterKey,
        resolvedApplovinCodes,
        codesForRequest,
      );
      if (applovinFilterList === null || applovinFilterList.length === 0) {
        return skipPayload;
      }

      if (
        regionMode === 'us' &&
        !validateUsMarketSelection(filterVal, codesForRequest)
      ) {
        return skipPayload;
      }

      const base = kpiDimensionMetricsTrendBeforeRequest(data);
      if (base === false) {
        return skipPayload;
      }

      return mergeApplovinRegionIntoTrendPayload(base, {
        regionMode,
        filterVal,
        applovinCustomFilterKey,
        applovinFilterList,
        osList,
        codesConfig: codesForRequest,
      });
    },
    [
      applovinCustomFilterKey,
      resolveNormalizedCodesForMode,
      resolvedApplovinCodes,
      segmentOptionsFlat,
      skipPayload,
    ],
  );

  const applovinWrappedApi = useMemo(() => {
    const base =
      apiFromParent ??
      (userConfig && typeof userConfig === 'object' ? userConfig.api : undefined) ??
      DEFAULT_OVERALL_PERFORMANCE_API;

    const resolveEmpty = () =>
      Promise.resolve(createApplovinEmptyOverallPerformanceResponse());

    if (typeof base === 'function') {
      return (params: Record<string, unknown>, data: unknown) => {
        if (params?.[APPLOVIN_SKIP_REQUEST_KEY]) {
          return resolveEmpty();
        }
        return (base as (p: Record<string, unknown>, d: unknown) => Promise<unknown>)(
          params,
          data,
        );
      };
    }

    const url = String(base);

    return (params: Record<string, unknown>) => {
      if (params?.[APPLOVIN_SKIP_REQUEST_KEY]) {
        return resolveEmpty();
      }
      return dashboardRequest({
        url,
        method: 'post',
        data: params,
      });
    };
  }, [apiFromParent, userConfig]);

  const trackData = useMemo(
    () => [
      ...DIMENSION_DEFAULT_TRACK_DATA,
      'applovinRegionTab',
      'applovinCodesFingerprint',
    ],
    [],
  );

  const mergedConfig = useMemo(
    () => ({
      ...(userConfig && typeof userConfig === 'object' ? userConfig : {}),
      beforeRequest,
    }),
    [userConfig, beforeRequest],
  );

  const segmentNode =
    segmentVisible && segmentOptions.length > 0 ? (
      <div
        className={
          applovinRegionTabConfig?.segmentClassName ||
          'dbd-kpi-applovin-region-trend-service__segment'
        }
      >
        <Segment
          value={segmentValue}
          onChange={(v: string) => setSegmentValue(v)}
          options={segmentOptions}
        />
      </div>
    ) : undefined;

  return (
    <KpiOverallPerformanceDimensionTrendService
      {...(rest as Record<string, unknown>)}
      // api 可接函数或 URL，下游 Service 已兼容；此处按字符串签名断言以匹配 props 声明
      api={applovinWrappedApi as unknown as string}
      className={classNames(
        'dbd-kpi-applovin-region-trend-service',
        classNameProp as string | undefined,
      )}
      config={mergedConfig}
      contentHeader={segmentNode}
      data={{
        applovinRegionTab: segmentValue,
        applovinCodesFingerprint,
      }}
      trackData={trackData}
    />
  );
};

export default KpiApplovinRegionTrendService;
export type { IApplovinRegionCodesByMode } from './applovinRegionTrendUtils';
